import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TCPlayer from 'tcplayer.js'
import 'tcplayer.js/dist/tcplayer.min.css'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { usePIP } from './usePIP'
import {
  selectDisplayFinishEventCountdownstate,
  selectDisplayStartEventCountdownstate,
} from '../reducers/callSlice'
import { useParams } from 'react-router-dom'
import {
  setAutoPlayUnlocker,
  selectAutoPlayUnlocker,
  clearAutoPlayUnmuteRequest,
  selectAudioPermissionGranted,
  setAudioPermissionGranted,
  setTencentConnectionDialog,
} from '../reducers/uiSlice'
import { selectCurrentStore } from '../reducers/storeSlice'
import { isDemoVideoStore, getDemoVideoUrl } from '../utils/demoVideoStores'

// Los eventos del player son ~20 por sesión y cada uno imprimía en la consola del
// espectador en producción. Se conservan para depurar, detrás de una bandera que se
// evalúa en cada llamada para poder encenderla sin reconstruir el bundle.
export const arePlayerLogsEnabled = () =>
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_TENCENT_PLAYER_LOGS === 'true'

const playerLog = (...args) => {
  if (arePlayerLogsEnabled()) console.debug('[TCPlayer]', ...args)
}

/**
 * Códigos del canal de WebRTC que interesan para distinguir los dos estados que se
 * parecen desde afuera pero significan cosas opuestas:
 *
 *   sano      : 1007 -> 1008 (señalización exitosa) -> 1001 -> 1002 -> 1003, y frames
 *   bloqueado : 1007 -> 1005 -> 1007 -> 1005... ~1/s, y NUNCA llega el 1008
 *
 * `1005` suelto es un hipo de red normal; lo que constituye evidencia de que este
 * espectador no puede recibir es el BUCLE sin llegar nunca a reproducir.
 *
 * MEDIDO en navegador, y es lo que evita el falso positivo más caro de este producto:
 *
 *   | escenario                        | llegó 1008 | racha sostenida | frames |
 *   |----------------------------------|------------|-----------------|--------|
 *   | evento que no arrancó            | SÍ         | sí (4 en 24 s)  | 0      |
 *   | red que bloquea WebRTC           | NO         | sí (11 en 30 s) | 0      |
 *   | sano                             | SÍ         | no              | 522    |
 *
 * O sea: la racha SOLA no alcanza —un evento que no arrancó también la produce, y ahí no
 * hay ninguna falla que informar—. Lo que separa los dos casos es que la señalización
 * llegue a completarse. Por eso el cartel exige racha sostenida Y ausencia de 1008, y no
 * necesita consultar nada del camino HLS.
 */
const WEBRTC_SIGNALING_OK_CODE = 1008
const WEBRTC_RECONNECTING_CODE = 1005

/** Ocurrencias mínimas, y ventana que tienen que abarcar, para considerarlo sostenido. */
const WEBRTC_RECONNECT_STREAK_MIN = 3
const WEBRTC_RECONNECT_SPAN_MIN_MS = 5000

/**
 * Antes de mostrar el cartel se espera esta gracia y se cancela si aparece imagen. Cubre
 * la red intermitente: alguien con mala conexión puede acumular reconexiones y conectar
 * bien un segundo después, y a ese espectador no hay nada que informarle.
 */
const CONNECTION_DIALOG_GRACE_MS = 5000

/**
 * Errores de LICENCIA de TCPlayer. Medido con los cinco dominios de licencia bloqueados —el
 * principal más cuatro respaldos que el SDK intenta en cascada—: emite `CODE:56` y **no crea
 * el elemento `<video>`**, así que no hay reproducción posible por este camino.
 *
 * No se puede detectar con la firma del canal: la señalización SÍ se completa (llega el
 * 1008) y nunca hay bucle de reconexión. Es un modo de falla distinto y necesita su propia
 * evidencia.
 */
const TCPLAYER_LICENSE_ERROR_CODES = new Set([50, 51, 52, 53, 54, 55, 56])

/**
 * Si el SDK no creó el `<video>` en esta ventana, no lo va a crear. Cubre el caso genérico
 * de «el SDK no arrancó» aunque no llegue ningún código de error usable.
 */
const TCPLAYER_BOOTSTRAP_TIMEOUT_MS = 6000

/**
 * Diagnóstico observable en producción, sin ensuciar la consola del espectador. Permite
 * mirar el estado del canal en el navegador de un organizador que reporta que no ve nada,
 * sin pedirle que reconstruya nada ni activar banderas de build.
 */
const publishDiagnostics = (data) => {
  if (typeof window === 'undefined') return
  window.__gjfWebRtcDiag = { ...(window.__gjfWebRtcDiag || {}), ...data }
}

/**
 * Sin frames nuevos en esta ventana, habiendo habido imagen, se vuelve al spinner.
 * Mismo criterio y mismo valor que en el camino HLS (ver el detalle allá).
 *
 * Medido en producción el 28 ago 2026 con dos espectadores sobre el mismo corte: en este
 * camino el `error` de TCPlayer reasigna la fuente y eso RESETEA el `<video>` a los ~5 s,
 * así que el espectador no ve un frame congelado sino NEGRO — y con el umbral en 60 s ese
 * negro duraba ~63 s sin spinner ni mensaje, que se lee como «se rompió la app». Bajarlo es
 * lo único que acorta esa ventana; la reconexión del SDK no la toca este número.
 */
const WEBRTC_FROZEN_TIMEOUT_MS = 12000

/** Cada cuánto se mira si el video presenta frames nuevos. */
const WEBRTC_FROZEN_CHECK_MS = 1000

export const useWebRtcPlayback = (setStreamingInProgress, streamingInProgress) => {
  const { eventId: roomId } = useParams()
  const { setCanPIP, setEnabled } = usePIP()
  const { postHogTrackEvent, postHogRegister } = usePostHogAnalytics()
  const dispatch = useDispatch()
  const displayFinishEventCountdown = useSelector(selectDisplayFinishEventCountdownstate)
  const displayStartEventCountdown = useSelector(selectDisplayStartEventCountdownstate)
  const autoPlayUnlocker = useSelector(selectAutoPlayUnlocker)
  const playerRef = useRef(null)
  const hasTrackedNetworkBlockedRef = useRef(false)
  const audioPermissionGranted = useSelector(selectAudioPermissionGranted)
  const store = useSelector(selectCurrentStore)
  const audioPermissionGrantedRef = useRef(audioPermissionGranted)
  // Cancelación del watcher de frames y memoria de que ya hubo imagen real.
  const frameWatcherRef = useRef(null)
  const hasPresentedFrameRef = useRef(false)
  // Evidencia del canal: racha de reconexiones y si la señalización llegó a completarse.
  const reconnectCountRef = useRef(0)
  const firstReconnectAtRef = useRef(undefined)
  const signalingOkRef = useRef(false)
  const dialogShownRef = useRef(false)
  const dialogTimerRef = useRef(null)

  const trackNetworkBlockedOnce = () => {
    if (hasTrackedNetworkBlockedRef.current) {
      return
    }

    hasTrackedNetworkBlockedRef.current = true
    postHogTrackEvent('live-network-blocked', {
      eventId: roomId,
      companyId: store?.company?.id,
    })
  }

  /**
   * `canplay` sólo dice que hay metadata suficiente para empezar: no prueba que se esté
   * viendo algo. Declarar la transmisión activa ahí oculta el spinner y deja al
   * espectador frente a una pantalla negra sin ninguna señal de que algo falló.
   * La evidencia de que hay imagen es un frame efectivamente presentado.
   */
  const resolveVideoElement = (player) => {
    let root = null
    try {
      // `player.el()` tira si el player ya fue dispuesto.
      root = typeof player?.el === 'function' ? player.el() : null
    } catch (_error) {
      root = null
    }
    if (root) {
      if (typeof root.matches === 'function' && root.matches('video')) return root
      const nested = typeof root.querySelector === 'function' ? root.querySelector('video') : null
      if (nested) return nested
    }
    const byId = document.getElementById('tcplayer-video')
    if (byId?.tagName === 'VIDEO') return byId
    return byId?.querySelector?.('video') ?? document.querySelector('video')
  }

  const stopFrameWatcher = () => {
    if (frameWatcherRef.current) {
      frameWatcherRef.current()
      frameWatcherRef.current = null
    }
  }

  const markStreamingWithRealFrame = (player) => {
    if (hasPresentedFrameRef.current) return
    const video = resolveVideoElement(player)
    if (!video) {
      // NUNCA declarar la transmisión activa sin elemento donde comprobar imagen: eso es
      // exactamente la pantalla negra silenciosa que este hook viene a eliminar. Con la
      // licencia bloqueada el SDK no crea el `<video>` y `canplay` igual puede llegar.
      // Se deja al watchdog de arranque decidir, que sí informa al espectador.
      playerLog('sin elemento de video: no se declara activa la transmisión')
      return
    }

    const confirm = () => {
      if (hasPresentedFrameRef.current) return
      hasPresentedFrameRef.current = true
      stopFrameWatcher()
      playerLog('primer frame presentado: la transmisión está activa')
      publishDiagnostics({ everPresentedFrame: true, sustainedReconnectLoop: false })
      cancelConnectionDialog()
      setStreamingInProgress(true)
    }

    if (typeof video.requestVideoFrameCallback === 'function') {
      const handle = video.requestVideoFrameCallback(confirm)
      frameWatcherRef.current = () => {
        try {
          video.cancelVideoFrameCallback?.(handle)
        } catch (error) {
          playerLog('no se pudo cancelar el watcher de frames', error)
        }
      }
      return
    }

    // Safari viejo y navegadores sin requestVideoFrameCallback: se mira el contador de
    // frames decodificados, que es acumulativo, así que se compara contra el inicial.
    const initialFrames = video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0
    const intervalId = setInterval(() => {
      const current = video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0
      if (current > initialFrames) confirm()
    }, 250)
    frameWatcherRef.current = () => clearInterval(intervalId)
  }

  /**
   * El cartel informa, no rescata: no conmuta de protocolo ni recarga. El protocolo es una
   * decisión de configuración de la compañía y un espectador no la cambia para el resto de
   * la audiencia.
   */
  /** Abre el cartel una sola vez y lo registra. */
  const showConnectionDialog = ({ errorCode, errorMessage, extra }) => {
    if (dialogShownRef.current) return
    dialogShownRef.current = true
    publishDiagnostics({ dialogReason: errorMessage })
    postHogTrackEvent('live-webrtc-unreachable', {
      eventId: roomId,
      companyId: store?.company?.id,
      reason: errorMessage,
      errorCode,
      ...extra,
    })
    dispatch(setTencentConnectionDialog({ open: true, errorCode, errorMessage }))
  }

  const scheduleConnectionDialog = (span) => {
    if (dialogShownRef.current || dialogTimerRef.current) return

    dialogTimerRef.current = setTimeout(() => {
      dialogTimerRef.current = null
      // Si en la gracia apareció imagen, no hay nada que informar.
      if (hasPresentedFrameRef.current || signalingOkRef.current) return

      playerLog('el canal no puede recibir: se informa al espectador')
      showConnectionDialog({
        errorCode: WEBRTC_RECONNECTING_CODE,
        errorMessage: 'webrtc_signaling_never_completed',
        extra: { reconnectCount: reconnectCountRef.current, reconnectSpanMs: span },
      })
    }, CONNECTION_DIALOG_GRACE_MS)
  }

  const cancelConnectionDialog = () => {
    if (dialogTimerRef.current) {
      clearTimeout(dialogTimerRef.current)
      dialogTimerRef.current = null
    }
    // Si el cartel llegó a abrirse y ahora hay imagen, se cierra: el diagnóstico dejó de
    // ser cierto y un modal tapa el video, el chat y el carrito.
    if (dialogShownRef.current) {
      dialogShownRef.current = false
      dispatch(setTencentConnectionDialog({ open: false }))
    }
  }

  /**
   * iOS rechaza `play()` si se desmutea sin un gesto del usuario, y al rechazarlo
   * DETIENE el video. Un desmuteo que falla tiene que volver a mutear y seguir
   * reproduciendo: es preferible sin audio que en negro.
   */
  const playWithAudioIfAllowed = (player) => {
    if (!player) return
    const wantsAudio = audioPermissionGrantedRef.current
    if (wantsAudio) player.muted(false)
    const played = player.play()
    if (played?.catch) {
      played.catch((error) => {
        playerLog('play() rechazado, se reintenta en silencio', error)
        player.muted(true)
        player.play()?.catch?.((silentError) => {
          playerLog('play() rechazado incluso muteado', silentError)
        })
      })
    }
  }

  const destroyPlayer = () => {
    setStreamingInProgress(false)
    stopFrameWatcher()
    hasPresentedFrameRef.current = false
    if (playerRef.current) {
      try {
        playerRef.current.dispose()
      } catch (error) {
        console.error('[TCPlayer] error disposing player', error)
      }
      playerRef.current = null
    }
  }

  const isDemoStore = () => isDemoVideoStore(store)

  const buildSourceUrl = () => {
    if (isDemoStore()) {
      return getDemoVideoUrl()
    }
    return `${process.env.REACT_APP_TENCENT_PLAYBACK_URL}/${roomId}?tabr_bitrates=FHD,HD,SD,Smooth&tabr_start_bitrate=SD&tabr_control=auto`
  }

  useEffect(() => {
    if (!roomId) return

    const createPlayer = () => {
      playerLog('create player')
      const player = TCPlayer('tcplayer-video', {
        sources: [{ src: buildSourceUrl() }],
        licenseUrl: process.env.REACT_APP_TENCENT_PLAYER_LICENSE_URL,
        language: 'en',
        autoplay: true,
        playsinline: true,
        muted: true,
        controls: false,
        // Una transmisión en vivo no se repite: `loop` sobre un stream reinicia los
        // timestamps y rompe cualquier medición de progreso. Sólo aplica al demo.
        loop: isDemoStore(),
        errorDisplay: false,
        webrtcConfig: {
          connectRetryCount: Infinity,
          connectRetryDelay: 3,
          fallback: false,
          showLog: false,
        },
        width: '100%',
        height: '100%',
      })
      ;[
        'play',
        'playing',
        'loadstart',
        'loadedmetadata',
        'loadeddata',
        'progress',
        'canplaythrough',
        'pause',
        'ratechange',
        'seeked',
        'seeking',
        'timeupdate',
        'volumechange',
        'waiting',
        'ended',
      ].forEach((eventName) => {
        player.on(eventName, () => playerLog(`${eventName} event`))
      })

      player.on('canplay', () => {
        playerLog('canplay event')
        if (audioPermissionGrantedRef.current) {
          playWithAudioIfAllowed(player)
        } else {
          dispatch(
            setAutoPlayUnlocker({
              open: true,
            }),
          )
        }
        // No se declara activa la transmisión hasta que haya un frame real.
        markStreamingWithRealFrame(player)
      })
      player.on('error', (event) => {
        playerLog('error event', event)
        const current = playerRef.current
        if (!current) return

        const rawCode = current.error?.()?.code ?? event?.code
        const code = Number(rawCode)
        if (TCPLAYER_LICENSE_ERROR_CODES.has(code)) {
          // La licencia no se recupera reintentando: sin ella el SDK no crea el `<video>`.
          // Reasignar la fuente en bucle sobre un player sin elemento no lleva a nada.
          playerLog('error de licencia', code)
          showConnectionDialog({ errorCode: code, errorMessage: 'tcplayer_license_error' })
          return
        }

        // Para el resto, reasignar la fuente y reintentar sin límite es intencional: la
        // señal de un evento va y viene (pruebas de audio y video previas), y rendirse
        // dejaría al espectador sin ver el evento real.
        current.src(buildSourceUrl())
        playWithAudioIfAllowed(current)
      })
      player.on('resolutionswitching', () => {
        playerLog('resolution switching', player.webrtcStats)
      })
      player.on('resolutionswitched', () => {
        playerLog('resolution switched', player.webrtcStats)
      })
      player.on('fullscreenchange', () => {
        playerLog('fullscreen change', player.webrtcStats)
      })
      player.on('webrtcevent', (event) => {
        playerLog('webrtc event', event)
        const code = event?.data?.code
        if (code === undefined) return

        if (code === WEBRTC_SIGNALING_OK_CODE) {
          signalingOkRef.current = true
          publishDiagnostics({ signalingOk: true })
        }

        if (code !== WEBRTC_RECONNECTING_CODE) return

        // Si ya hubo imagen, este espectador SÍ puede recibir: la racha no cuenta.
        if (hasPresentedFrameRef.current) return

        reconnectCountRef.current += 1
        if (firstReconnectAtRef.current === undefined) firstReconnectAtRef.current = Date.now()
        const span = Date.now() - firstReconnectAtRef.current
        const sostenido =
          reconnectCountRef.current >= WEBRTC_RECONNECT_STREAK_MIN &&
          span >= WEBRTC_RECONNECT_SPAN_MIN_MS

        publishDiagnostics({
          reconnectCount: reconnectCountRef.current,
          reconnectSpanMs: span,
          sustainedReconnectLoop: sostenido,
          signalingOk: signalingOkRef.current,
          everPresentedFrame: hasPresentedFrameRef.current,
        })

        if (sostenido && !signalingOkRef.current) {
          // Se trackea con la misma evidencia que el cartel: un 1005 suelto es un hipo de
          // red normal y disparaba «network blocked» en sesiones sanas, inflando la métrica.
          trackNetworkBlockedOnce()
          scheduleConnectionDialog(span)
        }
      })
      player.on('webrtcstats', () => {
        playerLog('webrtc stats', player.webrtcStats)
      })

      return player
    }

    let bootstrapTimer = null

    if (streamingInProgress === false) {
      if (!playerRef.current) {
        try {
          playerRef.current = createPlayer()
          // Una vez por sesión de reproducción: el $pageview del live queda como
          // denominador cortable por protocolo/motor, sin eventos nuevos.
          postHogRegister({ playbackProtocol: 'webrtc', playbackEngine: 'tcplayer' })
        } catch (error) {
          console.error('[TCPlayer] error creating player', error)
          showConnectionDialog({ errorMessage: 'tcplayer_constructor_threw' })
        }
      }

      // La ausencia del elemento es la señal más fiable de que el SDK no arrancó, y con la
      // licencia bloqueada es la ÚNICA: ahí no hay bucle de reconexión y la señalización
      // se completa igual, así que la firma del canal no lo ve.
      bootstrapTimer = setTimeout(() => {
        bootstrapTimer = null
        if (hasPresentedFrameRef.current) return
        if (resolveVideoElement(playerRef.current)) return
        playerLog('el SDK no montó el elemento de video')
        publishDiagnostics({ videoElementMissing: true })
        showConnectionDialog({ errorMessage: 'tcplayer_no_video_element' })
      }, TCPLAYER_BOOTSTRAP_TIMEOUT_MS)
    }

    const handleVcommMsg = (event) => {
      if (event.data?.type === 'MUTE_AUDIO') {
        playerRef.current && playerRef.current.muted(true)
      }
      if (event.data?.type === 'UNMUTE_AUDIO') {
        playerRef.current && playerRef.current.muted(false)
      }
    }

    /**
     * El camino no bajaba `streamingInProgress` jamás una vez que hubo imagen: al cortar
     * la señal sin finalizar el evento, el último frame quedaba congelado para siempre
     * (medido: `data-active` en true 170+ s después del corte). Si la señal vuelve, la
     * propia actividad del video lo reactiva — no hay nada que rearmar. La actividad se
     * mide con dos señales: contador de frames y reloj (ver el detalle en el intervalo).
     */
    let lastFramesFrozen = null
    let lastClockFrozen = null
    let lastFrameAdvanceAt = Date.now()
    const frozenIntervalId = setInterval(() => {
      if (!hasPresentedFrameRef.current) return
      if (document.visibilityState !== 'visible') {
        lastFrameAdvanceAt = Date.now()
        return
      }
      const video = resolveVideoElement(playerRef.current)
      if (!video) return
      const frames = video.getVideoPlaybackQuality?.().totalVideoFrames ?? null
      const reloj = typeof video.currentTime === 'number' ? video.currentTime : null
      // Dos señales de actividad, cualquiera alcanza: que el contador de frames cambie
      // (el player lo recrea al recuperarse y vuelve a 0, así que sirve el CAMBIO, no
      // superar el máximo) o que avance el reloj. En Safari el contador queda clavado
      // en 0 incluso reproduciendo (medido con TCPlayer: readyState 4 y reloj subiendo
      // con totalVideoFrames en 0), así que con una sola de las dos se disparaba el
      // spinner en plena reproducción sana y el cartel no se retraía jamás.
      const framesCambio = frames !== null && frames !== lastFramesFrozen
      const relojAvanzo = reloj !== null && lastClockFrozen !== null && reloj > lastClockFrozen
      if (frames !== null) lastFramesFrozen = frames
      if (reloj !== null) lastClockFrozen = reloj
      if (framesCambio || relojAvanzo) {
        lastFrameAdvanceAt = Date.now()
        setStreamingInProgress(true)
        return
      }
      if (Date.now() - lastFrameAdvanceAt > WEBRTC_FROZEN_TIMEOUT_MS) {
        setStreamingInProgress(false)
      }
    }, WEBRTC_FROZEN_CHECK_MS)

    let resumeTimeoutId = null
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!playerRef.current) return
      resumeTimeoutId = setTimeout(() => {
        const current = playerRef.current
        if (!current) return
        if (current.paused() !== true) return
        current.src(buildSourceUrl())
        playWithAudioIfAllowed(current)
      }, 3000)
    }

    // `visibilitychange` se emite en `document`. Registrarlo en `window` funciona sólo
    // por propagación, y este handler es el que recupera la reproducción al volver del
    // segundo plano en mobile, que es la mayor parte de la audiencia.
    document.addEventListener('visibilitychange', handleVisibilityChange)

    window.addEventListener('message', handleVcommMsg)

    return () => {
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId)
      if (bootstrapTimer) clearTimeout(bootstrapTimer)
      clearInterval(frozenIntervalId)
      if (dialogTimerRef.current) clearTimeout(dialogTimerRef.current)
      dialogTimerRef.current = null
      dialogShownRef.current = false
      destroyPlayer()
      window.removeEventListener('message', handleVcommMsg)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [roomId])

  useEffect(() => {
    hasTrackedNetworkBlockedRef.current = false
  }, [roomId, store?.company?.id])

  useEffect(() => {
    if (streamingInProgress && !displayFinishEventCountdown && !displayStartEventCountdown) {
      setCanPIP(true)
    } else {
      setEnabled(false)
      setCanPIP(false)
    }
  }, [streamingInProgress, displayFinishEventCountdown, displayStartEventCountdown])

  // Mantener el ref sincronizado con el estado
  useEffect(() => {
    audioPermissionGrantedRef.current = audioPermissionGranted
  }, [audioPermissionGranted])

  // Escuchar cuando se solicita unmute desde el diálogo
  useEffect(() => {
    if (autoPlayUnlocker.requestUnmute) {
      try {
        if (playerRef.current) {
          // El desmuteo viene de un gesto del usuario sobre el diálogo, así que acá sí
          // está habilitado; se marca antes para que `playWithAudioIfAllowed` lo respete.
          audioPermissionGrantedRef.current = true
          playWithAudioIfAllowed(playerRef.current)
          markStreamingWithRealFrame(playerRef.current)
        }
      } catch (error) {
        console.error('[TCPlayer] error unmuting player', error)
      } finally {
        dispatch(clearAutoPlayUnmuteRequest())
        dispatch(setAudioPermissionGranted(true))
      }
    }
  }, [autoPlayUnlocker.requestUnmute, dispatch])

  return null
}
